import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Storage, translate } from 'react-jhipster';
import 'app/app.scss';
import { getEntities } from './bank.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Avatar, TableBody } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Search from '@mui/icons-material/Search';
import { Clear } from '@mui/icons-material';
import axios from 'axios';

export const RebateBankList = (props: RouteComponentProps) => {
  const dispatch = useAppDispatch();
  const [bankList, setBankList] = useState([]);
  const loading = useAppSelector(state => state.bank.loading);
  const [searchOnChange, setSearchOnChange] = useState('');
  const [searchItem, setSearchItem] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pageNo, setPage] = useState(0);

  const [s3BucketName, setS3BucketName] = useState<string | null>(null);
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/config/bucketName');
        setS3BucketName(response.data.bucketName);
      } catch (error) {
        console.error('Error fetching bucket name:', error);
      }
    };
    fetchConfig();
  }, []);

  // How many rebate settings each bank has per commodity type (ALSHMOKH-56).
  const [settingCounts, setSettingCounts] = useState<Record<string, { internationalCount: number; localCount: number }>>({});
  useEffect(() => {
    axios
      .get('/api/rebate-settings/counts-by-bank')
      .then(res => {
        const map: Record<string, { internationalCount: number; localCount: number }> = {};
        (res.data || []).forEach((c: { bankId: number | string; internationalCount: number; localCount: number }) => {
          map[String(c.bankId)] = { internationalCount: c.internationalCount || 0, localCount: c.localCount || 0 };
        });
        setSettingCounts(map);
      })
      .catch(error => {
        console.error('Error fetching rebate setting counts:', error);
      });
  }, []);

  useEffect(() => {
    dispatch(getEntities({ page: 0, size: 0, sort: 'name,asc', search: searchItem })).then(res => {
      setBankList(res.payload['data']);
    });
  }, [searchItem]);

  const handleSearchItem = () => {
    setPage(0);
    setSearchItem(searchOnChange);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'rgba(255,255,255,0)',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      letterSpacing: 1,
      textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      textAlign: Storage.session.get('locale') === 'en' ? 'left' : 'right',
    },
  }));

  const StyledTableCellCenter = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'rgba(255,255,255,0)',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      letterSpacing: 1,
      textAlign: 'center',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    cursor: 'pointer',
  }));

  const rows = bankList.map(obj => ({
    ...obj,
    imgURL: obj.imgURL !== null ? `https://${s3BucketName}.s3.eu-west-3.amazonaws.com/bankLogo/${obj.imgURL}` : null,
  }));

  const openRebate = (bankId: number) => props.history.push(`/bank/${bankId}/rebate`);

  return (
    <Container>
      <div className="mt-4 mb-4">
        <Typography variant="h4" component="div" data-cy="rebateBankListHeading" className="mb-4">
          {translate('comexApp.bank.rebate.bankListTitle')}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ margin: '30px 0px' }}>
          <Grid container>
            <Grid item md={4} sm={6} xs={12}>
              <TextField
                size="small"
                value={searchOnChange}
                placeholder={translate('home.search')}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      onClick={() => {
                        setSearchOnChange('');
                        setSearchItem('');
                        setPage(0);
                      }}
                    >
                      <Clear style={{ fontSize: 12, cursor: 'pointer' }} />
                    </InputAdornment>
                  ),
                }}
                onChange={e => setSearchOnChange(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') handleSearchItem();
                }}
              />
            </Grid>
          </Grid>
        </Stack>
        <Paper className="paper-table mb-4">
          <TableContainer sx={{ maxHeight: 440 }}>
            {rows && rows.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow className="enhanced-table-bank" sx={{ fontWeight: '500' }}>
                    <StyledTableCellCenter>{translate('comexApp.bank.imgURL')}</StyledTableCellCenter>
                    <StyledTableCell>{translate('comexApp.bank.name')}</StyledTableCell>
                    <StyledTableCell>{translate('comexApp.bank.description')}</StyledTableCell>
                    <StyledTableCellCenter>{translate('comexApp.bank.rebate.settingsColumn')}</StyledTableCellCenter>
                    <StyledTableCellCenter>{translate('comexApp.bank.action')}</StyledTableCellCenter>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(pageNo * rowsPerPage, pageNo * rowsPerPage + rowsPerPage).map(bank => (
                    <StyledTableRow key={bank?.id} onClick={() => openRebate(bank.id)}>
                      <StyledTableCell>
                        {bank?.imgURL === null ? (
                          <Avatar style={{ margin: 'auto' }}>{bank.name.charAt(0)}</Avatar>
                        ) : (
                          <Avatar alt={bank.name} src={bank?.imgURL} style={{ margin: 'auto' }}>
                            <img style={{ width: '100%' }} />
                          </Avatar>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>{bank?.name}</StyledTableCell>
                      <StyledTableCell>{bank?.description}</StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ textAlign: 'center' }}>
                          {translate('comexApp.branch.settings.tabs.international')}: {settingCounts[String(bank?.id)]?.internationalCount ?? 0}
                          {' · '}
                          {translate('comexApp.branch.settings.tabs.local')}: {settingCounts[String(bank?.id)]?.localCount ?? 0}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell className="text-end">
                        <div className="btn-group flex-btn-group-container" style={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title={translate('comexApp.bank.home.rebate')}>
                            <IconButton
                              className="px-3"
                              color="primary"
                              aria-label="rebate"
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                openRebate(bank.id);
                              }}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              !loading && (
                <div className="alert alert-warning">{translate('comexApp.bank.home.notFound')}</div>
              )
            )}
          </TableContainer>
          <TablePagination
            className={Storage.session.get('locale') === 'en' ? 'table-pagination' : 'table-pagination-arabic'}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={pageNo}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            labelRowsPerPage={translate('home.table.rowsPerPage')}
          />
        </Paper>
      </div>
    </Container>
  );
};

export default RebateBankList;
